import { PackageUpgradeModal } from "./OfferGate";
import { useFetchHotel } from "../redux/hooks";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Text,
  Button,
  Spinner,
  Layer,
  Heading,
  Tip,
  Accordion,
  AccordionPanel,
} from "grommet";
import {
  Trash,
  Document,
  Image,
  Link,
  Close,
  Edit,
  Checkmark,
  CircleAlert,
  Clock,
  StatusGood,
} from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentAlertPrompt } from "@shared/Alert";
import { KnowledgeBaseService } from "../../../services";
import { useTranslation } from "react-i18next";

const POLL_INTERVAL_MS = 5000;

// ── Status badge ──────────────────────────────────────────────────────────────

function KbStatusBadge({ status, onClick }) {
  const { t } = useTranslation();
  const config = {
    pending: {
      bg: "#f59e0b",
      label: t("knowledgeBase.statusPending"),
      icon: <Clock size="small" color="white" />,
      cursor: "default",
    },
    processing: {
      bg: "#3b82f6",
      label: t("knowledgeBase.statusProcessing"),
      icon: <Spinner size="xsmall" />,
      cursor: "default",
    },
    to_validate: {
      bg: "#8b5cf6",
      label: t("knowledgeBase.statusToValidate"),
      icon: <CircleAlert size="small" color="white" />,
      cursor: onClick ? "pointer" : "default",
    },
    generating: {
      bg: "#3b82f6",
      label: t("knowledgeBase.statusSaving"),
      icon: <Spinner size="xsmall" />,
      cursor: "default",
    },
    ready: {
      bg: "#10b981",
      label: t("knowledgeBase.statusActive"),
      icon: <StatusGood size="small" color="white" />,
      cursor: "default",
    },
    error: {
      bg: "#ef4444",
      label: t("knowledgeBase.statusError"),
      icon: <CircleAlert size="small" color="white" />,
      cursor: "default",
    },
  };
  const { bg, label, icon, cursor } = config[status] || {
    bg: "#9ca3af",
    label: status,
    icon: null,
    cursor: "default",
  };

  return (
    <Box
      round="xlarge"
      pad={{ horizontal: "small", vertical: "3px" }}
      background={bg}
      align="center"
      justify="center"
      direction="row"
      gap="xsmall"
      onClick={onClick || undefined}
      style={{ cursor, userSelect: "none" }}
    >
      {icon}
      <Text size="xsmall" weight="bold" color="white">
        {label}
      </Text>
    </Box>
  );
}

// ── Source type icon ──────────────────────────────────────────────────────────

function KbTypeIcon({ doc }) {
  if (doc.type === "url") return <Link size="medium" color="plain" />;
  if (doc.mimeType?.startsWith("image/"))
    return <Image size="medium" color="plain" />;
  return <Document size="medium" color="plain" />;
}

// ── Q&A Pairs Modal ───────────────────────────────────────────────────────────

function PairsModal({ doc, internal, onClose, onDocUpdated }) {
  const { hotel } = useFetchHotel();
  const [upgrade, setUpgrade] = useState(false);
  const { t } = useTranslation();
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [movingId, setMovingId] = useState(null);

  const canGenerate = doc.status === "to_validate";

  useEffect(() => {
    KnowledgeBaseService.getKnowledgeBasePairs(doc._id).then(
      (res) => {
        setPairs(res.data || []);
        setLoading(false);
      },
      (err) => {
        presentToastErrorContent(err);
        setLoading(false);
        onClose();
      },
    );
  }, [doc._id, onClose]);

  const handleDeletePair = (pair) => {
    presentAlertPrompt({
      title: t("knowledgeBase.deleteQATitle"),
      message: t("knowledgeBase.deleteQAMessage", { question: pair.question }),
      onOK: async () => {
        setDeletingId(pair._id);
        try {
          const res = await KnowledgeBaseService.deletePair(doc._id, pair._id);
          setPairs((prev) => prev.filter((p) => p._id !== pair._id));
          onDocUpdated({ ...doc, qaCount: res.data.qaCount });
        } catch (err) {
          presentToastErrorContent(err);
        } finally {
          setDeletingId(null);
        }
      },
      buttonOKText: t("knowledgeBase.deleteConfirm"),
      buttonOKColor: "status-critical",
    });
  };

  const handleMovePair = async (pair) => {
    presentAlertPrompt({
      title: t("knowledgeBase.moveQATitle"),
      message: t("knowledgeBase.moveQAMessage"),
      onOK: async () => {
        setMovingId(pair._id);
        try {
          const res = await KnowledgeBaseService.movePair(doc._id, pair._id);
          setPairs((prev) => prev.filter((p) => p._id !== pair._id));
          onDocUpdated({ ...doc, qaCount: res.data.qaCount });
          presentToastSuccess(t("knowledgeBase.moveQASuccess"));
        } catch (err) {
          presentToastErrorContent(err);
        } finally {
          setMovingId(null);
        }
      },
      buttonOKText: t("knowledgeBase.moveQAConfirm"),
    });
  };

  const handleGenerate = async () => {
    if (internal && !hotel?.aiEnabled) { setUpgrade(true); return; }
    setGenerating(true);
    try {
      const res = await KnowledgeBaseService.generateEmbeddings(doc._id);
      // Backend accepted the job — close immediately, poller will update status
      onDocUpdated(res.data.document);
      presentToastSuccess(t("knowledgeBase.saving"));
      onClose();
    } catch (err) {
      presentToastErrorContent(err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Layer onEsc={onClose} onClickOutside={onClose} position="center">
      {upgrade && <PackageUpgradeModal capability={internal ? "ai" : "aiGuest"} onClose={() => setUpgrade(false)} />}
      <Box width="large" style={{ maxHeight: "85vh" }} flex={false}>
        {/* Header */}
        <Box
          direction="row"
          justify="between"
          align="center"
          pad={{ horizontal: "medium", vertical: "medium" }}
          border={{ side: "bottom", color: "light-3" }}
          flex={false}
        >
          <Box gap="xsmall" fill>
            <Heading level="4" margin="none">
              {doc.name}
            </Heading>
            <Box direction="row" align="center" gap="small">
              <KbStatusBadge status={doc.status} />
              {!loading && (
                <Text size="xsmall" color="dark-4">
                  {t("knowledgeBase.questionCount", { count: pairs.length })}
                </Text>
              )}
            </Box>
          </Box>
          <Button icon={<Close />} onClick={onClose} plain />
        </Box>

        {/* Body */}
        <Box
          overflow={{ vertical: "auto" }}
          flex
          pad={{ horizontal: "medium", top: "small", bottom: "xsmall" }}
        >
          {loading ? (
            <Box align="center" pad="large">
              <Spinner />
            </Box>
          ) : pairs.length === 0 ? (
            <Box align="center" pad="large">
              <Text size="small" color="dark-4">
                {t("knowledgeBase.noQAPairs")}
              </Text>
            </Box>
          ) : (
            <Accordion fill="horizontal">
              {pairs.map((pair) => (
                <AccordionPanel
                  key={pair._id}
                  label={
                    <Box
                      direction="row"
                      align="center"
                      pad="small"
                      gap="small"
                      fill
                    >
                      <Box fill>
                        <Text size="small" weight="bold" truncate>
                          {pair.question}
                        </Text>
                      </Box>
                      {/* Actions — stop propagation so accordion doesn't toggle */}
                      <Box
                        direction="row"
                        align="center"
                        gap="small"
                        width={{ min: "70px" }}
                        flex={false}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tip
                          content={t("knowledgeBase.moveToQA")}
                          dropProps={{ align: { bottom: "top" } }}
                        >
                          <Button
                            icon={
                              movingId === pair._id ? (
                                <Spinner size="xsmall" />
                              ) : (
                                <Edit size="small" />
                              )
                            }
                            size="small"
                            primary
                            color="#8b5cf6"
                            onClick={() =>
                              !movingId && !deletingId && handleMovePair(pair)
                            }
                            disabled={!!movingId || !!deletingId}
                          />
                        </Tip>
                        <Tip
                          content={t("knowledgeBase.deleteQATip")}
                          dropProps={{ align: { bottom: "top" } }}
                        >
                          <Button
                            icon={
                              deletingId === pair._id ? (
                                <Spinner size="xsmall" />
                              ) : (
                                <Trash size="small" />
                              )
                            }
                            size="small"
                            primary
                            color="status-critical"
                            onClick={() =>
                              !movingId && !deletingId && handleDeletePair(pair)
                            }
                            disabled={!!movingId || !!deletingId}
                          />
                        </Tip>
                      </Box>
                    </Box>
                  }
                >
                  <Box
                    pad={{ horizontal: "small", vertical: "small" }}
                    background="light-1"
                    border={{ side: "top", color: "light-3" }}
                  >
                    <Text size="small">{pair.answer}</Text>
                  </Box>
                </AccordionPanel>
              ))}
            </Accordion>
          )}
        </Box>

        {/* Footer */}
        <Box
          direction="row"
          justify="between"
          align="center"
          pad={{ horizontal: "medium", vertical: "medium" }}
          border={{ side: "top", color: "light-3" }}
          gap="small"
          flex={false}
        >
          {canGenerate ? (
            <Button
              primary
              label={
                generating
                  ? t("knowledgeBase.saving")
                  : t("knowledgeBase.saveKB")
              }
              icon={
                generating ? (
                  <Spinner size="xsmall" />
                ) : (
                  <Checkmark size="small" />
                )
              }
              onClick={handleGenerate}
              disabled={generating || loading || pairs.length === 0}
              color="status-ok"
            />
          ) : (
            <Box />
          )}
          <Button plain label={t("knowledgeBase.close")} onClick={onClose} />
        </Box>
      </Box>
    </Layer>
  );
}

// ── Main list ────────────────────────────────────────────────────────────────

export default function KnowledgeBaseList({ refreshSignal, internal = false }) {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const pollRef = useRef(null);
  const [modalDoc, setModalDoc] = useState(null);

  const fetchList = useCallback(() => {
    KnowledgeBaseService.getKnowledgeBase(internal).then(
      (response) => setList(response.data || []),
      (error) => presentToastErrorContent(error),
    );
  }, [internal]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshSignal]);

  // Poll while any item is still processing
  useEffect(() => {
    const hasActive = list.some(
      (d) =>
        d.status === "pending" ||
        d.status === "processing" ||
        d.status === "generating",
    );
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(fetchList, POLL_INTERVAL_MS);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [list, fetchList]);

  const handleDocUpdated = useCallback((updatedDoc) => {
    setList((prev) =>
      prev.map((d) => (d._id === updatedDoc._id ? { ...d, ...updatedDoc } : d)),
    );
  }, []);

  const handleDelete = (doc) => {
    presentAlertPrompt({
      title: t("knowledgeBase.deleteTitle"),
      message: t("knowledgeBase.deleteMessage", { name: doc.name }),
      onOK: () => {
        KnowledgeBaseService.deleteKnowledgeBase(doc._id).then(
          () => {
            presentToastSuccess(t("knowledgeBase.deletedSuccess"));
            fetchList();
          },
          (error) => presentToastErrorContent(error),
        );
      },
      buttonOKText: t("knowledgeBase.deleteConfirm"),
      buttonOKColor: "status-critical",
    });
  };

  const canOpenModal = (doc) =>
    (doc.status === "to_validate" || doc.status === "ready") && doc.qaCount > 0;

  if (list.length === 0) {
    return (
      <Box pad="medium" align="center">
        <Text size="small" color="dark-4">
          {t("knowledgeBase.noSources")}
        </Text>
      </Box>
    );
  }

  return (
    <Box gap="xsmall" fill>
      {/* Modal */}
      {modalDoc && (
        <PairsModal
          internal={internal}
          doc={list.find((d) => d._id === modalDoc) || modalDoc}
          onClose={() => setModalDoc(null)}
          onDocUpdated={handleDocUpdated}
        />
      )}

      {/* Column header */}
      <Box
        direction="row"
        align="center"
        pad={{ horizontal: "small", vertical: "xsmall" }}
        gap="small"
        fill="horizontal"
        background="light-2"
        round="xsmall"
      >
        <Box width="28px" flex={false} />
        <Box fill>
          <Text weight="bold" size="xsmall" color="dark-2">
            {t("knowledgeBase.nameColumn")}
          </Text>
        </Box>
        <Box width="120px" flex={false} align="center">
          <Text weight="bold" size="xsmall" color="dark-2">
            {t("knowledgeBase.statusColumn")}
          </Text>
        </Box>
        <Box width="44px" flex={false} align="center">
          <Text weight="bold" size="xsmall" color="dark-2">
            {t("knowledgeBase.qaColumn")}
          </Text>
        </Box>
        <Box width="36px" flex={false} />
      </Box>

      {/* Rows */}
      {list.map((doc) => {
        const isActive =
          doc.status !== "pending" && doc.status !== "processing";
        const openable = canOpenModal(doc);

        return (
          <Box
            key={doc._id}
            direction="row"
            align="center"
            pad={{ horizontal: "small", vertical: "small" }}
            gap="small"
            fill="horizontal"
            round="xsmall"
            background="white"
            border={{ color: "light-3" }}
            onClick={openable ? () => setModalDoc(doc._id) : undefined}
            style={{
              opacity: isActive ? 1 : 0.7,
              cursor: openable ? "pointer" : "default",
            }}
          >
            {/* Icon */}
            <Box width="28px" align="center" justify="center" flex={false}>
              <KbTypeIcon doc={doc} />
            </Box>

            {/* Name + meta */}
            <Box fill style={{ minWidth: 0 }}>
              <Text size="small" weight="bold" truncate>
                {doc.name}
              </Text>
              {doc.type === "url" && (
                <Text size="xsmall" color="dark-4" truncate>
                  {doc.sourceUrl}
                </Text>
              )}
              {doc.status === "error" && doc.errorMessage && (
                <Text size="xsmall" color="status-critical" truncate>
                  {doc.errorMessage}
                </Text>
              )}
            </Box>

            {/* Status */}
            <Box width="120px" align="center" flex={false}>
              <KbStatusBadge status={doc.status} />
            </Box>

            {/* Q/A count */}
            <Box width="44px" align="center" flex={false}>
              {openable ? (
                <Box
                  round="xlarge"
                  background="light-3"
                  pad={{ horizontal: "small", vertical: "2px" }}
                  align="center"
                >
                  <Text size="small" weight="bold" color="dark-2">
                    {doc.qaCount}
                  </Text>
                </Box>
              ) : doc.status === "pending" || doc.status === "processing" ? (
                <Spinner size="xsmall" />
              ) : (
                <Text size="small" color="dark-4">
                  —
                </Text>
              )}
            </Box>

            {/* Delete */}
            <Box
              width="36px"
              align="center"
              flex={false}
              onClick={(e) => e.stopPropagation()}
            >
              <Tip content={t("knowledgeBase.deleteSourceTip")}>
                <Button
                  plain
                  icon={<Trash size="small" color="dark-4" />}
                  onClick={() => handleDelete(doc)}
                  style={{ padding: "4px" }}
                />
              </Tip>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
